// src/pages/CircleDashboard.jsx
//
// One Friend Circle. Since October 2026 a circle holds no writing
// (docs/CIRCLES_PULSE.md). It shows:
//   - who's in it and what it's on (CircleFocus);
//   - one fixed-option question everyone answers before anyone sees an
//     answer (CirclePulse);
//   - questions to talk about in person, with nowhere to type (TalkAboutThis);
//   - the invite code, how to share it privately, and a "new code" button;
//   - the way out, for anyone, including whoever started it.
//
// Gone with the written features: the reflection feed, Question of the Day,
// comments, reactions, the "every member" weekly challenges, the presence dot,
// the five-seat count and the three-member lock on the modules.
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Users } from 'lucide-react';
import { useCirclesStore } from '../stores/circlesStore';
import friendCirclesService from '../services/circles/FriendCirclesService';
import { useApp } from '../context/AppContext';
import CircleFocus from '../components/circles/CircleFocus';
import CirclePulse from '../components/circles/CirclePulse';
import TalkAboutThis from '../components/circles/TalkAboutThis';
import CircleInviteCard from '../components/circles/CircleInviteCard';
import LeaveCircle from '../components/circles/LeaveCircle';

export default function CircleDashboard({ userId }) {
  const {
    myCircles, activeCircleId, getActiveCircle, loadMyCircles, refreshCircle, leaveCircle,
  } = useCirclesStore();
  const { setCurrentView, isModuleCompleted } = useApp();
  const [isLoading, setIsLoading] = useState(true);

  const activeCircle = getActiveCircle();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId) return;
      setIsLoading(true);
      await loadMyCircles(userId);
      if (!cancelled) setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId, loadMyCircles]);

  // No circles after loading: go and make or join one.
  useEffect(() => {
    if (!isLoading && myCircles && myCircles.length === 0) {
      setCurrentView('friend-circles-setup');
    }
  }, [isLoading, myCircles, setCurrentView]);

  const refresh = useCallback(() => {
    if (activeCircleId) refreshCircle(activeCircleId);
  }, [activeCircleId, refreshCircle]);

  // If you've finished the module the circle is on, tick yourself off - the
  // module screen does this when you finish, and this catches a module you
  // finished before the circle picked it.
  const currentModule = activeCircle?.settings?.currentModule ?? null;
  const myModulesRead = activeCircle?.memberStatus?.[userId]?.modulesRead;
  useEffect(() => {
    if (!activeCircle?.id || !userId || currentModule == null) return;
    if ((myModulesRead || []).includes(currentModule)) return;
    if (typeof isModuleCompleted !== 'function' || !isModuleCompleted(currentModule)) return;
    friendCirclesService.recordModuleRead(activeCircle.id, userId, currentModule)
      .then((res) => { if (res.success) refresh(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCircle?.id, userId, currentModule, myModulesRead]);

  if (isLoading && !activeCircle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-500 mx-auto mb-4" />
          <p className="text-purple-200">Loading your circle…</p>
        </div>
      </div>
    );
  }

  if (!activeCircle) return null;

  // Names come from the circle's own memberStatus map, so nobody needs to
  // read anyone else's user document.
  const memberNames = Object.fromEntries((activeCircle.members || []).map((uid) => [
    uid, activeCircle.memberStatus?.[uid]?.username || 'A friend',
  ]));
  const memberCount = activeCircle.members?.length || 0;

  // A circle formed while a group was getting on outlives the group getting
  // on. After two quiet months it says so - once, gently, to people already
  // inside it - and offers the door. It only ever offers: nothing closes on
  // its own, and nobody is told that anyone else left.
  const isQuiet = friendCirclesService.isDormant(activeCircle);

  const handleLeave = () => leaveCircle(userId, activeCircle.id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-20 sm:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-12 max-w-3xl">
        {isQuiet && (
          <div className="mb-8 rounded-xl border border-white/15 bg-white/5 p-5">
            <p className="text-white/90 text-sm sm:text-base">
              This circle has been quiet for a couple of months.
            </p>
            <p className="mt-2 text-white/60 text-sm">
              That is allowed. Groups go quiet for all sorts of ordinary reasons, and a quiet circle is not a
              verdict on anyone in it. It is here whenever you want it, or you can step out, which nobody
              else is told about.
            </p>
            <div className="mt-4">
              <LeaveCircle circle={activeCircle} onLeave={handleLeave} />
            </div>
          </div>
        )}

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full text-4xl sm:text-5xl mb-5 shadow-2xl">
            <span aria-hidden="true">{activeCircle.emoji || '🌟'}</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-3 break-words">
            {activeCircle.name}
          </h1>
          <p className="text-lg text-purple-200 flex items-center justify-center gap-2">
            <Users aria-hidden="true" className="w-5 h-5" />
            {memberCount} {memberCount === 1 ? 'person' : 'people'} in this circle
          </p>
          {myCircles.length > 1 && (
            <button
              type="button"
              onClick={() => setCurrentView('my-circles')}
              className="mt-3 text-sm text-purple-200 underline underline-offset-4 hover:text-white"
            >
              Switch circle
            </button>
          )}
        </motion.header>

        <div className="space-y-6 mb-10">
          <CircleFocus
            circle={activeCircle}
            userId={userId}
            memberNames={memberNames}
            onChanged={refresh}
          />
          <CirclePulse
            circle={activeCircle}
            userId={userId}
            memberNames={memberNames}
            onChanged={refresh}
          />
          <TalkAboutThis circle={activeCircle} />
          <CircleInviteCard circle={activeCircle} />
        </div>

        <div className="text-center mb-10">
          <button
            type="button"
            onClick={() => setCurrentView('modules')}
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-xl text-lg font-bold bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-2xl transition-colors"
          >
            <BookOpen aria-hidden="true" className="w-5 h-5" />
            Go to the modules
            <ArrowRight aria-hidden="true" className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        <footer className="border-t border-white/10 pt-6 text-center space-y-3">
          <p className="text-xs text-white/50">
            Nobody can remove anyone else from a circle. Anyone can leave, any time.
          </p>
          <LeaveCircle circle={activeCircle} onLeave={handleLeave} />
        </footer>
      </div>
    </div>
  );
}
